import React, { useRef, useState, useEffect } from "react";
import { Box } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import CustomTypography from "../../globalStyles/fonts"; // Adjust path as needed
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import Popper from "@mui/material/Popper";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { isMobile } from "../../globalStyles/mobile";
import { useDarkMode } from "../context/darkModeContext";
import { formatDate } from "../../utils/formatDate";

// Colors picked from screenshot (adjust if needed)
const COLORS = {
  Accepted: "#3BBDC4",
  Fulfilled: "#72D0CB",
  Delivered: "#057C82",
  Cancelled: "#AFDCDE",
  Onhold: "#DFDFDF",
};

const OrdersStatus = ({ enableCalendar = false, data }) => {
  const { colors } = useDarkMode();

  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date("2022-06-13"));
  const calendarRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const [ticks, setTicks] = useState([0, 100, 200, 300, 400, 500]);
  const [maxTick, setMaxTick] = useState(500);

  // Close calendar on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        calendarContainerRef.current &&
        !calendarContainerRef.current.contains(event.target) &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target)
      ) {
        setShowCalendar(false);
      }
    }
    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  useEffect(() => {
    if (data && data.length > 0) {
      // Calculate max value from all data points
      const maxValue = Math.max(
        ...data.map(
          (x) =>
            (x.Accepted || 0) +
            (x.Cancelled || 0) +
            (x.Delivered || 0) +
            (x.Fulfilled || 0) +
            (x.Onhold || 0) +
            (x.Shipped || 0),
        ),
      );

      // Add 10% buffer to max value
      const calculatedMax = Math.ceil(maxValue * 1.1);

      // Set max tick
      setMaxTick(calculatedMax);

      // Generate 5 evenly spaced ticks from 0 to max
      const newTicks = [];
      for (let i = 0; i <= 5; i++) {
        newTicks.push(Math.round((calculatedMax * i) / 5));
      }

      setTicks(newTicks);
    } else {
      // Default values when no data
      setMaxTick(500);
      setTicks([0, 100, 200, 300, 400, 500]);
    }
  }, [data]);

  return (
    <Box
      sx={{
        background: colors.bgPaper,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        p: 2,
        width: "100%",
        minHeight: 350,
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <CustomTypography
          variant="subtitle1"
          fontWeight={700}
          letterSpacing={1}
          sx={{ fontSize: isMobile() ? 12 : 14 }}
        >
          ORDERS
        </CustomTypography>
        {enableCalendar ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              background: colors.bgPaper,
              fontSize: 14,
              color: colors.textPrimary,
              gap: 1,
              cursor: "pointer",
            }}
            ref={calendarRef}
            onClick={() => setShowCalendar((prev) => !prev)}
          >
            <CalendarMonthOutlinedIcon
              sx={{ fontSize: 18, color: colors.textTertiary }}
            />
            <span style={{ fontSize: 12, color: colors.textSecondary }}>
              {formatDate(selectedDate, "Select Date")}
            </span>
          </Box>
        ) : (
          <></>
        )}
        <Popper
          open={showCalendar}
          anchorEl={calendarRef.current}
          placement="bottom-start"
          disablePortal
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 4],
              },
            },
          ]}
          sx={{ zIndex: 20000 }} // Ensures popper is on top of everything
        >
          <Box
            ref={calendarContainerRef}
            sx={{
              bgcolor: colors.bgPaper,
              boxShadow: 3,
              borderRadius: 1,
              p: 1,
              zIndex: 20000,
              width: 300,
              "& .react-calendar": {
                width: "100%",
              },
            }}
          >
            <Calendar
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e);
                setShowCalendar(false);
              }}
              maxDate={new Date()}
              className="custom-calendar"
              defaultActiveStartDate={selectedDate ?? new Date()}
            />
          </Box>
        </Popper>
      </Box>
      {
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data} barCategoryGap={10} maxBarSize={50}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: colors.textPrimary }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: colors.textTertiary }}
              domain={[0, maxTick]}
              ticks={ticks}
              tickFormatter={(value) =>
                ticks.includes(value) && value !== 0
                  ? value
                  : value === 0
                    ? ""
                    : null
              }
              label={{
                value: "Order Count",
                angle: -90,
                position: "insideLeft",
                offset: 10,
                style: {
                  textAnchor: "middle",
                  fill: colors.textTertiary,
                  fontSize: 10,
                },
              }}
            />
            <Tooltip
              cursor={false}
              contentStyle={{
                backgroundColor: "rgba(51, 51, 51, 0.5)", // Reduced opacity from 0.9 to 0.8
                border: "none",
                borderRadius: "6px",
                color: "#fff",
                fontSize: "11px", // Reduced font size by 2px from default (usually 13px)
                fontWeight: "600", // Added font weight
              }}
            />
            <Bar dataKey="Accepted" stackId="a" fill={COLORS.Accepted} />
            <Bar dataKey="Fulfilled" stackId="a" fill={COLORS.Fulfilled} />
            <Bar dataKey="Delivered" stackId="a" fill={COLORS.Delivered} />
            <Bar dataKey="Cancelled" stackId="a" fill={COLORS.Cancelled} />
            <Bar
              dataKey="Onhold"
              stackId="a"
              fill={COLORS.Onhold}
              radius={[10, 10, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      }
      <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: COLORS.Accepted,
            }}
          />
          <CustomTypography variant="caption" sx={{ ml: 0.5, fontSize: 10 }}>
            Accepted
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: COLORS.Fulfilled,
            }}
          />
          <CustomTypography variant="caption" sx={{ ml: 0.5, fontSize: 10 }}>
            Fulfilled
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: COLORS.Delivered,
            }}
          />
          <CustomTypography variant="caption" sx={{ ml: 0.5, fontSize: 10 }}>
            Delivered
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: COLORS.Cancelled,
            }}
          />
          <CustomTypography variant="caption" sx={{ ml: 0.5, fontSize: 10 }}>
            Cancelled
          </CustomTypography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: COLORS.Onhold,
            }}
          />
          <CustomTypography variant="caption" sx={{ ml: 0.5, fontSize: 10 }}>
            Onhold
          </CustomTypography>
        </Box>
      </Box>
    </Box>
  );
};

export default OrdersStatus;
